/**
 * BreakawayStrip — Sep 2026. "7am to 10am Breakaway" Dashboard strip.
 *
 * Reads the pre-computed scan from GET /api/dashboard/breakaway (refreshed
 * every 5 min by `services/breakaway_scanner.py` between 6:30–10:15 ET).
 * A pick = $5–$15 stock, gap-up vs prior close, latest minute bar ≥ 3× its
 * normal per-minute volume, float ≤ 20M, and news dated today.
 */
import { Fragment, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Rocket, HelpCircle, Timer, Newspaper, Loader2, RefreshCw, History, Zap, Flame, Thermometer } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import BreakawayBacktestDialog from '@/components/BreakawayBacktestDialog';
import BreakawayNewsModal from '@/components/BreakawayNewsModal';
import DividendFilterSegmentedControl from '@/components/DividendFilterSegmentedControl';
import SwipeHint, { useSwipeHint } from '@/components/SwipeHint';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtM = (n) => (n ? `${(n / 1e6).toFixed(n < 10e6 ? 1 : 0)}M` : '—');
const fmtVol = (n) => (!n ? '—' : n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(0)}K` : String(n));
const fmtEt = (iso) => (iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) : '');

function useCountdown(targetIso) {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!targetIso) { setLeft(null); return undefined; }
    const tick = () => setLeft(Math.max(0, new Date(targetIso).getTime() - Date.now()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  if (left == null) return null;
  const s = Math.floor(left / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const pad = (x) => String(x).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

export default function BreakawayStrip({ onOpenStock }) {
  const [data, setData] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [newsPick, setNewsPick] = useState(null);
  const [info, setInfo] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [filter, setFilterState] = useState(() => {
    const saved = sessionStorage.getItem('breakawayFilter');
    return saved && ['dividend', 'all', 'non_dividend'].includes(saved) ? saved : 'dividend';
  });
  const setFilter = (v) => { setFilterState(v); sessionStorage.setItem('breakawayFilter', v); };
  const effectiveFilter = isAdmin ? filter : 'dividend';

  const load = () => axios.get(`${API}/dashboard/breakaway`, { timeout: 6000 })
    .then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);

  const win = data?.window || {};
  const scan = data?.scan || {};
  const settings = data?.settings || {};
  const phase = win.phase;
  const target = phase === 'live' ? (scan.next_run_at || null) : (win.opens_at || null);
  const countdown = useCountdown(target);
  if (!data) return null;
  const fresh = !scan.stale_day;
  const sameDay = phase === 'live' || phase === 'closed';
  const inUniverse = (r) => effectiveFilter === 'all' || (effectiveFilter === 'dividend' ? !!r.pays_dividend : !r.pays_dividend);
  const list = (k) => (phase === 'prescan' ? [] : sameDay ? (fresh ? (scan[k] || []) : []) : (scan[k] || [])).filter(inUniverse);
  const picks = list('picks');
  const hot = list('running_hot');
  const warm = list('getting_warmer');
  const near = list('near_misses');
  const pool = list('pool');
  const reviewMode = phase === 'closed' || phase === 'off';
  const anyTier = picks.length + hot.length + warm.length > 0;
  const trackedN = picks.length + hot.length + warm.length + near.length;
  const gapUpsN = pool.length + [...picks, ...hot, ...warm, ...near].filter((r) => r.gap_ok).length;

  const label = phase === 'live' ? 'Next scan in' : phase === 'prescan' ? 'Strip opens in' : 'Next session in';

  return (
    <div
      className="rounded-lg border border-emerald-700/45 dark:border-emerald-400/45 bg-card/80 px-2.5 py-2 shadow-[0_4px_12px_-2px_rgba(15,23,42,0.14),0_2px_4px_-1px_rgba(15,23,42,0.06)] dark:shadow-[0_1px_0_0_rgba(255,255,255,0.04)_inset,0_2px_8px_-2px_rgba(0,0,0,0.6)]"
      data-testid="breakaway-strip"
    >
      <div className="flex items-center justify-between mb-1.5 flex-wrap gap-x-3 gap-y-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Rocket className="w-4 h-4 text-emerald-500" />
          <h3 className="text-[18px] font-bold text-emerald-700 dark:text-emerald-400" data-testid="breakaway-title">
            7am to 10:30 am Breakaway
          </h3>
          <button
            type="button"
            onClick={() => setShowHelp((v) => !v)}
            className="text-muted-foreground/70 hover:text-foreground cursor-help"
            title="What qualifies a Breakaway pick?"
            data-testid="breakaway-help"
          >
            <HelpCircle className="w-3 h-3" />
          </button>
          <PhaseBadge phase={phase} />
          <DividendFilterSegmentedControl value={effectiveFilter} isAdmin={isAdmin} instanceId="breakaway" onChange={setFilter} />
          <span className="text-[11px] text-muted-foreground/80" data-testid="breakaway-universe-label">
            ${settings.price_min}–${settings.price_max} · float ≤ {fmtM(settings.float_max)}
          </span>
        </div>
        <span className="text-xs text-muted-foreground/80 inline-flex items-center gap-1.5" data-testid="breakaway-countdown">
          <Timer className="w-3.5 h-3.5 text-emerald-500" />
          {label}
          <span className="font-mono font-bold text-foreground tabular-nums" data-testid="breakaway-countdown-value">{countdown ?? '--:--'}</span>
          <span className="hidden sm:inline">· {phase === 'live' || phase === 'prescan' ? `every ${settings.interval_min} min` : `opens ${fmtEt(win.opens_at)} ET`}</span>
        </span>
      </div>

      {showHelp && (
        <div className="mb-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs text-muted-foreground leading-relaxed" data-testid="breakaway-help-panel">
          Every {settings.interval_min} minutes from 6:30 to 10:45 AM ET the scanner compares each stock&apos;s latest minute
          bar to its normal per-minute volume (20-day average ÷ 390). A stock is <strong>tracked</strong> once a minute prints
          ≥ {settings.rvol_min}× normal volume ({settings.open_bar_rvol_min ?? 5}× for the 9:30–9:35 opening bars) while trading ${settings.price_min}–${settings.price_max} and sitting at or near yesterday&apos;s close.
          Ladder: <strong>BREAKAWAY</strong> = gap up · float ≤ {fmtM(settings.float_max)} (≤ {fmtM(settings.float_pref)} = MICRO) · news since the prior close ·
          <strong> RUNNING HOT</strong> = float + news in place, gap not positive yet (within {Math.abs(settings.gap_close_pct ?? 2)}%) ·
          <strong> GETTING WARMER</strong> = float in place, no news found yet. News comes from Massive + Finnhub company news + Finnhub's swept general/merger market feed (checked every scan; Yahoo is not used here). Quotes are 15-min delayed, so
          the 7:00 AM strip reflects trading through ~6:45. Not a recommendation — a starting point for your own research.
        </div>
      )}

      <AdminControls settings={settings} onChanged={load} filter={effectiveFilter} />

      {phase === 'prescan' && (
        <p className="text-xs text-muted-foreground/80 py-1" data-testid="breakaway-prescan-msg">
          Pre-scanning the market now — picks unlock at 7:00 AM ET.
        </p>
      )}
      {phase !== 'prescan' && !anyTier && (
        <p className="text-xs text-muted-foreground/80 py-1" data-testid="breakaway-empty-msg">
          {phase === 'live'
            ? `Nothing on the ladder yet — ${trackedN} tracked at ${settings.rvol_min}× volume, ${gapUpsN} gap-ups in range. Rescanning every ${settings.interval_min} min.`
            : scan.date ? `Nothing reached the ladder on ${scan.date}. Scanner resumes ${fmtEt(win.opens_at)} ET.` : 'No scan has run yet.'}
        </p>
      )}
      {phase !== 'prescan' && anyTier && reviewMode && (
        <p className="text-[11px] text-muted-foreground/70 mb-1" data-testid="breakaway-session-label">
          Session over · everything found on {scan.date} (last scan {fmtEt(scan.generated_at)} ET) · clears at 7:00 AM ET next trading day
        </p>
      )}
      {phase !== 'prescan' && (
        <>
          <TierRow tier="breakaway" showPeak={reviewMode} rows={picks} settings={settings} onOpenStock={onOpenStock} onNews={setNewsPick} onInfo={setInfo} />
          <TierRow tier="running_hot" showPeak={reviewMode} rows={hot} settings={settings} onOpenStock={onOpenStock} onNews={setNewsPick} onInfo={setInfo} />
          <TierRow tier="getting_warmer" showPeak={reviewMode} rows={warm} settings={settings} onOpenStock={onOpenStock} onNews={setNewsPick} onInfo={setInfo} />
          {near.length > 0 && <NearMisses rows={near} settings={settings} onOpenStock={onOpenStock} onNews={setNewsPick} />}
          {pool.length > 0 && <GapPool rows={pool} settings={settings} onOpenStock={onOpenStock} />}
        </>
      )}
      <PillInfoDialog info={info} onClose={() => setInfo(null)} />
      <BreakawayNewsModal pick={newsPick} date={scan.date} open={!!newsPick} onOpenChange={(o) => { if (!o) setNewsPick(null); }} />
    </div>
  );
}

const TIERS = {
  breakaway: { label: 'Breakaway', Icon: Zap, sub: 'Gap up · 3× volume · small float · fresh news',
    hint: 'All five checks: price band · gap-up · 3× minute volume · float · news',
    title: 'text-emerald-700 dark:text-emerald-300', icon: 'text-emerald-500',
    chip: 'border-emerald-400/60 dark:border-emerald-500/40 bg-emerald-50/60 dark:bg-emerald-950/30' },
  running_hot: { label: 'Running Hot', Icon: Flame, sub: 'Volume, float and news in place — gap not positive yet',
    hint: '3× volume · float · news in place — gap not yet positive but close (within the close band)',
    title: 'text-orange-700 dark:text-orange-300', icon: 'text-orange-500',
    chip: 'border-orange-400/60 dark:border-orange-500/40 bg-orange-50/60 dark:bg-orange-950/30' },
  getting_warmer: { label: 'Getting Warmer', Icon: Thermometer, sub: 'Volume and float in place — no news found yet',
    hint: '3× volume · float in place — no news found yet (gap up or close)',
    title: 'text-amber-800 dark:text-amber-200', icon: 'text-amber-500',
    chip: 'border-amber-400/60 dark:border-amber-500/40 bg-amber-50/60 dark:bg-amber-950/30' },
};

const TIER_EXPLAINERS = {
  breakaway: (s) => ({
    title: 'Breakaway — every check passed',
    desc: 'A breakaway gap is a stock jumping out of its recent range on heavy volume with a real catalyst. These names cleared all five checks at the last scan.',
    rows: [
      ['Price band', `$${s.price_min}–$${s.price_max}`],
      ['Gap', 'Opened / trading above yesterday\'s close'],
      ['Volume', `≥ ${s.rvol_min}× normal minute volume (≥ ${s.open_bar_rvol_min ?? 5}× for the 9:30–9:35 bars)`],
      ['Float', `≤ ${fmtM(s.float_max)} (≤ ${fmtM(s.float_pref)} = MICRO)`],
      ['News', s.news_since === 'today' ? 'Dated today (ET)' : 'Since yesterday\'s 4:00 PM close'],
    ],
    note: 'Once a stock qualifies it stays on this row for the rest of the session, even if it fades — the chip dims when it is no longer gapping at the latest scan. Not a recommendation.',
  }),
  running_hot: (s) => ({
    title: 'Running Hot — everything but the gap',
    desc: 'Heavy volume, a small float and fresh news are all in place, but the stock is still trading at or just under yesterday\'s close.',
    rows: [
      ['Volume', `≥ ${s.rvol_min}× normal minute volume`],
      ['Float', `≤ ${fmtM(s.float_max)}`],
      ['News', 'Found in the news window'],
      ['Gap', `Not positive yet — within ${Math.abs(s.gap_close_pct ?? 2)}% below the prior close`],
    ],
    note: 'If the price pushes above yesterday\'s close on a later scan the stock is promoted to Breakaway automatically.',
  }),
  getting_warmer: (s) => ({
    title: 'Getting Warmer — volume and float, no news yet',
    desc: 'The stock tripped the volume trigger inside the price band with a small float, but neither Massive, Finnhub company news nor the swept Finnhub market feed has an article for it yet.',
    rows: [
      ['Volume', `≥ ${s.rvol_min}× normal minute volume`],
      ['Float', `≤ ${fmtM(s.float_max)}`],
      ['News', 'None found — rechecked every scan'],
      ['Gap', 'Up, or within the close band'],
    ],
    note: 'Unexplained volume is often a leak ahead of a press release — or a pump. When a headline lands the stock moves up to Running Hot or Breakaway.',
  }),
};

const TIER_ROW_CAP = 20;

function TierRow({ tier, rows, settings, showPeak, onOpenStock, onNews, onInfo }) {
  const [showAll, setShowAll] = useState(false);
  const listRef = useRef(null);
  const [hint, updateHint] = useSwipeHint(listRef, [rows, showAll, showPeak]);
  if (!rows.length) return null;
  const t = TIERS[tier];
  const ordered = showPeak
    ? [...rows].sort((a, b) => (b.peak_pct ?? -Infinity) - (a.peak_pct ?? -Infinity))
    : rows;
  const visible = showAll ? ordered : ordered.slice(0, TIER_ROW_CAP);
  return (
    <div className="mb-2" data-testid={`breakaway-tier-${tier}`}>
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <t.Icon className={`w-3.5 h-3.5 shrink-0 ${t.icon}`} />
        <button type="button" onClick={() => onInfo?.(TIER_EXPLAINERS[tier](settings))}
          className={`text-[11px] font-bold uppercase tracking-wider cursor-help hover:underline ${t.title}`} title={t.hint} data-testid={`breakaway-tier-label-${tier}`}>
          {t.label} ⓘ
        </button>
        <span className="text-[11px] text-muted-foreground">{t.sub} · {rows.length}</span>
        {rows.length > TIER_ROW_CAP && (
          <button type="button" onClick={() => setShowAll((v) => !v)}
            className="text-[11px] text-sky-700 dark:text-sky-400 hover:underline" data-testid={`breakaway-tier-showall-${tier}`}>
            {showAll ? `show top ${TIER_ROW_CAP}` : `showing top ${TIER_ROW_CAP} by ${showPeak ? 'peak %' : 'volume'} · show all ${rows.length}`}
          </button>
        )}
      </div>
      <div
        ref={listRef}
        onScroll={updateHint}
        className="flex gap-2 overflow-x-auto pb-1 -mx-0.5 px-0.5 snap-x snap-mandatory sm:snap-none"
        data-testid={`breakaway-tier-list-${tier}`}
      >
        {visible.map((p) => <PickChip key={p.ticker} p={p} tier={tier} settings={settings} showPeak={showPeak} onOpenStock={onOpenStock} onNews={onNews} onInfo={onInfo} />)}
      </div>
      {visible.length > 1 && <SwipeHint hint={hint} testId={`breakaway-swipe-hint-${tier}`} />}
    </div>
  );
}

function PillInfoDialog({ info, onClose }) {
  return (
    <Dialog open={!!info} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="breakaway-pill-dialog">
        <DialogHeader className="pr-8 text-left">
          <DialogTitle className="leading-snug" data-testid="breakaway-pill-dialog-title">{info?.title}</DialogTitle>
          {info?.desc && <DialogDescription>{info.desc}</DialogDescription>}
        </DialogHeader>
        {info && (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
            {info.rows.map(([k, v]) => (
              <Fragment key={k}>
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="font-mono tabular-nums text-right">{v}</dd>
              </Fragment>
            ))}
          </dl>
        )}
        {info?.note && <p className="text-xs text-muted-foreground leading-relaxed">{info.note}</p>}
      </DialogContent>
    </Dialog>
  );
}

function GapPool({ rows, settings, onOpenStock }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-1" data-testid="breakaway-pool">
      <button type="button" onClick={() => setOpen((v) => !v)} className="text-[12px] text-sky-700 dark:text-sky-400 hover:underline" data-testid="breakaway-pool-toggle">
        <span className="font-bold">CLICK</span> <span className="text-[14px] align-[-1px]">{open ? '▾' : '▸'}</span> {rows.length} gap-ups in range (${settings.price_min}–${settings.price_max}) not yet at {settings.rvol_min}× volume
      </button>
      {open && (
        <div className="flex gap-1.5 flex-wrap mt-1" data-testid="breakaway-pool-list">
          {rows.map((r) => (
            <button
              key={r.ticker}
              type="button"
              onClick={() => onOpenStock?.(r.ticker)}
              className="text-[11px] px-2 py-0.5 rounded-full ring-1 ring-border bg-muted/40 hover:bg-muted"
              title={`${r.name || r.ticker} · $${Number(r.price).toFixed(2)} · gap +${r.gap_pct}% · ${r.minute_rvol}× last minute · ${r.day_rvol}× day`}
              data-testid={`breakaway-pool-${r.ticker}`}
            >
              <span className="font-bold">{r.ticker}</span> +{r.gap_pct}% · {r.minute_rvol}×
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NearMisses({ rows, settings, onOpenStock, onNews }) {
  const [open, setOpen] = useState(false);
  const reason = (r) => [
    !r.float_ok && (r.float_shares ? `float ${fmtM(r.float_shares)}` : 'no float data'),
    !r.news_ok && 'no news',
    !r.gap_ok && 'gap not up',
  ].filter(Boolean).join(' · ');
  return (
    <div className="mt-1" data-testid="breakaway-near-misses">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[12px] text-amber-700 dark:text-amber-400 hover:underline"
        data-testid="breakaway-near-toggle"
      >
        <span className="font-bold">CLICK</span> <span className="text-[14px] align-[-1px]">{open ? '▾' : '▸'}</span> {rows.length} tracked at {settings.rvol_min}× volume but float too big / no float data
      </button>
      {open && (
        <div className="flex gap-1.5 flex-wrap mt-1" data-testid="breakaway-near-list">
          {rows.map((r) => (
            <span key={r.ticker} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full ring-1 ring-border bg-muted/40">
              <button
                type="button"
                onClick={() => onOpenStock?.(r.ticker)}
                className="text-left hover:text-foreground"
                title={`${fmtEt(r.first_seen)} ET · $${Number(r.trigger_price || r.price).toFixed(2)} · ${r.trigger_rvol}× · gap +${r.gap_pct}% · missed: ${reason(r)}`}
                data-testid={`breakaway-near-${r.ticker}`}
              >
                <span className="font-bold">{r.ticker}</span> {fmtEt(r.first_seen)} · {r.trigger_rvol}× · +{r.gap_pct}%
                <span className="text-rose-600 dark:text-rose-400"> · {reason(r)}</span>
              </button>
              {r.news && (
                <button type="button" onClick={() => onNews?.(r)} className="text-sky-600 dark:text-sky-400 hover:text-sky-500" title="Read the news" data-testid={`breakaway-near-news-${r.ticker}`}>
                  <Newspaper className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function PhaseBadge({ phase }) {
  const map = {
    live: ['LIVE', 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 ring-emerald-500/30'],
    prescan: ['PRE-SCAN', 'bg-amber-500/15 text-amber-700 dark:text-amber-400 ring-amber-500/30'],
    closed: ['SESSION OVER', 'bg-slate-500/15 text-slate-600 dark:text-slate-300 ring-slate-500/30'],
    off: ['MARKET CLOSED', 'bg-slate-500/15 text-slate-600 dark:text-slate-300 ring-slate-500/30'],
  };
  const [text, cls] = map[phase] || map.off;
  return (
    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ring-1 ${cls}`} data-testid="breakaway-phase">
      {text}
    </span>
  );
}

function ChipSparkline({ path = [], prevClose, gapRef, ticker, onClick, rvolMin = 3 }) {
  const pts = path.filter((x) => Number.isFinite(x?.p));
  if (pts.length < 2 || !prevClose) return null;
  const W = 108, H = 24, PAD = 2;
  const gapAbs = Number.isFinite(gapRef) ? gapRef - prevClose : null;
  const gapLabel = gapAbs == null ? '' : gapAbs > 0 ? `+$${gapAbs.toFixed(2)} Gap Up` : gapAbs < 0 ? `-$${Math.abs(gapAbs).toFixed(2)} Below` : 'Flat open';
  const ys = [...pts.map((x) => x.p), prevClose];
  const min = Math.min(...ys), max = Math.max(...ys);
  const span = max - min || prevClose * 0.01;
  const X = (i) => PAD + (i * (W - PAD * 2)) / (pts.length - 1);
  const Y = (v) => H - PAD - ((v - min) / span) * (H - PAD * 2);
  const d = pts.map((x, i) => `${i === 0 ? 'M' : 'L'}${X(i).toFixed(1)},${Y(x.p).toFixed(1)}`).join(' ');
  const last = pts[pts.length - 1].p;
  const up = last >= prevClose;
  const stroke = up ? '#059669' : '#ea580c';
  const first = pts[0], lastPt = pts[pts.length - 1];
  // volume shading: one column per scan, opacity scales with minute RVOL (log), amber once ≥ rvolMin
  const vols = pts.map((x) => (Number.isFinite(x?.v) ? x.v : 0));
  const vMax = Math.max(rvolMin, ...vols);
  const colW = (W - PAD * 2) / Math.max(1, pts.length - 1);
  const peak = vols.reduce((bi, v, i) => (v > vols[bi] ? i : bi), 0);
  return (
    <svg
      width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
      role="button" tabIndex={0} onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); }}
      data-testid={`breakaway-chip-spark-${ticker}`}
    >
      <title>{`Scan-by-scan price · ${fmtEt(first.t)}–${fmtEt(lastPt.t)} ET · dashed = prior close $${Number(prevClose).toFixed(2)} · shading = minute volume (amber ≥ ${rvolMin}×, peak ${vols[peak].toFixed(1)}× at ${fmtEt(pts[peak].t)}) · click for the Today tab`}</title>
      {vols.map((v, i) => {
        if (v <= 0) return null;
        const hot = v >= rvolMin;
        const op = Math.min(0.85, 0.08 + 0.6 * (Math.log1p(v) / Math.log1p(vMax)));
        return (
          <rect
            key={i}
            x={(X(i) - colW / 2).toFixed(1)} y={0} width={colW.toFixed(1)} height={H}
            fill={hot ? '#f59e0b' : '#64748b'} fillOpacity={hot ? op : op * 0.5}
          />
        );
      })}
      <line x1={PAD} x2={W - PAD} y1={Y(prevClose)} y2={Y(prevClose)} stroke="currentColor" strokeOpacity="0.35" strokeDasharray="2 2" strokeWidth="1" />
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={X(pts.length - 1)} cy={Y(last)} r="1.8" fill={stroke} />
      {gapLabel && (
        <text
          x={W / 2} y={H / 2} textAnchor="middle" dominantBaseline="central"
          fontSize="8.5" fontWeight="700" fontFamily="ui-monospace, monospace"
          className={`[paint-order:stroke] stroke-white/80 dark:stroke-black/70 ${gapAbs > 0 ? 'fill-emerald-900 dark:fill-emerald-200' : 'fill-orange-900 dark:fill-orange-200'}`}
          strokeWidth="2"
          data-testid={`breakaway-chip-spark-gap-${ticker}`}
        >
          {gapLabel}
        </text>
      )}
    </svg>
  );
}

function PickChip({ p, tier = 'breakaway', settings = {}, showPeak = false, onOpenStock, onNews, onInfo }) {
  const perMin = Math.round((p.avg_volume || 0) / 390);
  const volInfo = () => onInfo?.({
    title: `${p.ticker} — ${p.minute_rvol}× normal volume`,
    desc: 'Latest minute bar compared with the stock\'s normal per-minute volume (20-day average daily volume ÷ 390 regular-session minutes).',
    rows: [
      ['Latest minute bar', `${fmtVol(p.minute_volume)} shares`],
      ['Normal per minute', `${fmtVol(perMin)} shares`],
      ['Minute multiple', `${p.minute_rvol}×`],
      ['Volume so far today', fmtVol(p.volume_today)],
      ['20-day avg daily volume', fmtVol(p.avg_volume)],
      ['Day so far vs average', `${p.day_rvol}×`],
      ['First tripped', `${fmtEt(p.first_seen)} ET at ${p.trigger_rvol}×`],
      ['Required', `${p.rvol_required ?? settings.rvol_min ?? 3}×${p.opening_bar ? ' (9:30–9:35 opening bar)' : ''}`],
      ['Bar time (15-min delayed)', `${fmtEt(p.bar_time)} ET`],
    ],
    note: `A stock joins the ladder once one minute prints at least ${settings.rvol_min ?? 3}× its normal volume (${settings.open_bar_rvol_min ?? 5}× for the 9:30–9:35 opening bars, which always run hot); it stays tracked for the rest of the session.`,
  });
  const money = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`);
  const gapKind = p.gap_pct <= 0 ? 'No gap-up yet' : p.full_gap ? 'Full gap' : 'Partial gap';
  const gapInfo = () => onInfo?.({
    title: `${p.ticker} — ${gapKind} (${p.gap_pct > 0 ? '+' : ''}${p.gap_pct}%)`,
    desc: 'A gap-up means today\'s price cleared yesterday\'s close, leaving empty space on the chart. Full gap = today\'s LOW is above yesterday\'s HIGH (no overlap at all). Partial gap = opened above the close but still inside yesterday\'s high–low range.',
    rows: [
      ['Prior close', money(p.prev_close)],
      ['Prior high', money(p.prev_high)],
      ['Prior low', money(p.prev_low)],
      ['Today\'s open', p.open ? money(p.open) : 'Pre-market (no regular open yet)'],
      ['Today\'s low', p.day_low ? money(p.day_low) : '—'],
      ['Latest price', money(p.price)],
      ['Gap vs prior close', `${p.gap_pct > 0 ? '+' : ''}${p.gap_pct}%`],
      ['Gap happened', p.gap_at
        ? (p.gap_kind === 'open'
          ? `At the 9:30 AM ET regular open (confirmed on the ${fmtEt(p.gap_at)} ET scan)`
          : p.gap_kind === 'tracked'
            ? `Already gapping when first tracked at ${fmtEt(p.gap_at)} ET`
            : `Pre-market — first seen on the ${fmtEt(p.gap_at)} ET scan (bar ${fmtEt(p.gap_bar_time)} ET, 15-min delayed)`)
        : p.gap_pct > 0 ? `Already gapping when first tracked at ${fmtEt(p.first_seen)} ET` : 'Not yet'],
      ['Type', gapKind + (p.full_gap ? ' — low above prior high' : p.gap_pct > 0 ? ' — inside prior range' : '')],
    ],
    note: p.gap_pct > 0
      ? 'There is no minimum % for a gap — even +0.5% counts if the price clears the prior close. Full gaps on heavy volume are the classic breakaway signature; partial gaps fill more often.'
      : p.open
        ? `Opened at ${money(p.open)} vs a ${money(p.prev_close)} prior close — no gap-up today (the gap is measured at the regular open). It now trades at ${money(p.price)}.`
        : `Pre-market: still at or below yesterday's close (within the ${Math.abs(settings.gap_close_pct ?? 2)}% close band). A push above ${money(p.prev_close)} promotes it.`,
  });
  const peakInfo = () => onInfo?.({
    title: `${p.ticker} — peak ${p.peak_pct > 0 ? '+' : ''}${p.peak_pct}% since trigger`,
    desc: 'How far the stock ran after it first tripped the volume trigger. Measured from the price at the trigger scan to the highest price seen on any later scan (one sample every 5 minutes, 15-min delayed — the true intraday high may be a little higher).',
    rows: [
      ['Trigger price', `$${Number(p.trigger_price).toFixed(2)} at ${fmtEt(p.first_seen)} ET`],
      ['Peak price', `$${Number(p.peak_price).toFixed(2)} at ${fmtEt(p.peak_at)} ET`],
      ['Peak gain', `${p.peak_pct > 0 ? '+' : ''}${p.peak_pct}%`],
      ['Last scan', `$${Number(p.price).toFixed(2)} (${p.now_pct > 0 ? '+' : ''}${p.now_pct}% from trigger)`],
      ['Gave back', p.peak_pct != null && p.now_pct != null ? `${(p.peak_pct - p.now_pct).toFixed(2)} pts from the peak` : '—'],
    ],
    note: 'After 10:15 the rows are sorted by this number so the day\'s best movers lead. Solid green = peaked 5% or more above the trigger.',
  });
  const divInfo = () => onInfo?.({
    title: `${p.ticker} pays a dividend`,
    desc: 'This stock is in the DiviDrip dividend universe, so it shows when the switch is on Dividend or All. Non-dividend names appear only under All / Non-Dividend.',
    rows: [
      ['Universe', 'Dividend payer'],
      ['Sector', p.sector || '—'],
      ['Market cap', p.market_cap ? `$${(p.market_cap / 1e6).toFixed(0)}M` : '—'],
    ],
    note: 'Click the ticker to open the full stock modal (dividend history, yield, safety).',
  });
  const floatInfo = () => onInfo?.({
    title: `${p.ticker} — ${p.float_tier === 'micro' ? 'MICRO' : 'SMALL'} float ${fmtM(p.float_shares)}`,
    desc: 'Free float = shares actually available to trade (excludes insiders, 5%+ holders and locked-up stock). Fewer shares means a burst of buying moves the price further.',
    rows: [
      ['Float', p.float_shares ? `${p.float_shares.toLocaleString()} shares` : '—'],
      ['Source', p.float_source === 'free_float' ? 'Massive free float' : p.float_source === 'weighted_shares' ? 'Shares outstanding (proxy)' : p.float_source || '—'],
      ['MICRO threshold', `≤ ${fmtM(settings.float_pref)}`],
      ['SMALL threshold', `≤ ${fmtM(settings.float_max)}`],
      ['Price', `$${Number(p.price).toFixed(2)}`],
      ['Prior close', `$${Number(p.prev_close).toFixed(2)}`],
      ['Gap', `${p.gap_pct > 0 ? '+' : ''}${p.gap_pct}%${p.full_gap ? ' (full gap — low above prior high)' : ''}`],
    ],
    note: 'Float ≤ 10M is tagged MICRO (preferred); ≤ 20M is SMALL. Anything larger drops to the "float too big" list.',
  });
  return (
    <div
      className={`shrink-0 snap-start w-[330px] rounded-md border px-2.5 py-1.5 ${TIERS[tier].chip} ${p.active === false ? 'opacity-70' : ''}`}
      title={p.active === false ? 'Qualified earlier today; no longer gapping up / in range at the last scan' : undefined}
      data-testid={`breakaway-chip-${p.ticker}`}
    >
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => onOpenStock?.(p.ticker)}
          className="font-bold text-sm text-foreground hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors"
          data-testid={`breakaway-chip-ticker-${p.ticker}`}
        >
          {p.ticker}
        </button>
        <button
          type="button"
          onClick={() => onOpenStock?.(p.ticker, 'stock-metrics')}
          className="text-xs font-mono tabular-nums hover:text-emerald-600 dark:hover:text-emerald-400 hover:underline transition-colors"
          title="Open Stock Metrics"
          data-testid={`breakaway-chip-price-${p.ticker}`}
        >
          ${Number(p.price).toFixed(2)}
        </button>
        <ChipSparkline path={p.path} prevClose={p.prev_close} gapRef={p.open || p.price} ticker={p.ticker} rvolMin={settings.rvol_min ?? 3} onClick={() => onOpenStock?.(p.ticker, 'intraday')} />
        <button type="button" onClick={gapInfo}
          className={`text-xs font-bold cursor-help hover:underline ${p.gap_pct > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-orange-600 dark:text-orange-400'}`}
          title="Click for the gap math (full vs partial)" data-testid={`breakaway-chip-gap-${p.ticker}`}>
          {p.gap_pct > 0 ? '+' : ''}{p.gap_pct}% gap{p.full_gap ? ' ●' : ''}
        </button>
      </div>
      <div className="flex items-center gap-1.5 mt-1 text-[10px] flex-wrap">
        <button type="button" onClick={volInfo}
          className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-700 dark:text-amber-400 font-bold cursor-help hover:bg-amber-500/25 transition-colors"
          title="Click for the volume math" data-testid={`breakaway-chip-vol-${p.ticker}`}>
          {p.trigger_rvol != null && Math.abs(p.trigger_rvol - p.minute_rvol) >= 0.5
            ? <>{p.trigger_rvol}× → now {p.minute_rvol}× ⓘ</>
            : <>{p.minute_rvol}× VOL ⓘ</>}
        </button>
        <button type="button" onClick={floatInfo}
          className={`px-1.5 py-0.5 rounded font-bold cursor-help transition-colors ${p.float_tier === 'micro' ? 'bg-rose-500/15 text-rose-700 dark:text-rose-400 hover:bg-rose-500/25' : 'bg-sky-500/15 text-sky-700 dark:text-sky-400 hover:bg-sky-500/25'}`}
          title="Click for float details" data-testid={`breakaway-chip-float-${p.ticker}`}>
          {p.float_tier === 'micro' ? 'MICRO' : 'SMALL'} {fmtM(p.float_shares)} ⓘ
        </button>
        {p.pays_dividend && (
          <button type="button" onClick={divInfo}
            className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 font-bold cursor-help hover:bg-emerald-500/25 transition-colors"
            title="Click — what DIV means here" data-testid={`breakaway-chip-div-${p.ticker}`}>
            DIV ⓘ
          </button>
        )}
        {showPeak && p.peak_pct != null && (
          <button
            type="button"
            onClick={peakInfo}
            className={`ml-auto px-1.5 py-0.5 rounded font-bold cursor-help transition-colors ${p.peak_pct >= 5 ? 'bg-emerald-600 text-white hover:bg-emerald-700' : p.peak_pct > 0 ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/25' : 'bg-slate-500/15 text-slate-600 dark:text-slate-300 hover:bg-slate-500/25'}`}
            title="Click for the peak math"
            data-testid={`breakaway-chip-peak-${p.ticker}`}
          >
            PEAK {p.peak_pct > 0 ? '+' : ''}{p.peak_pct}% ⓘ
          </button>
        )}
      </div>
      {!p.news && (
        <p className="mt-1 text-[10px] text-muted-foreground/70 italic" data-testid={`breakaway-chip-nonews-${p.ticker}`}>No news found yet — rechecked every scan</p>
      )}
      {p.news && (
        <button
          type="button"
          onClick={() => onNews?.(p)}
          className="mt-1 w-full flex items-start gap-1.5 text-left group"
          title={`${p.news.publisher} · ${fmtEt(p.news.published_utc)} ET — click to read all of today's news`}
          data-testid={`breakaway-chip-news-${p.ticker}`}
        >
          <span className="shrink-0 inline-flex items-center gap-0.5 px-1 py-px rounded border border-sky-500/50 bg-sky-500/10 text-sky-700 dark:text-sky-300 text-[9px] font-bold tracking-wider">
            <Newspaper className="w-2.5 h-2.5" /> NEWS{p.news.count > 1 ? ` ${p.news.count}` : ''}
          </span>
          <span className="text-[11px] text-muted-foreground group-hover:text-foreground leading-snug line-clamp-2">{p.news.title}</span>
        </button>
      )}
    </div>
  );
}

function AdminControls({ settings, onChanged, filter }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [showBacktest, setShowBacktest] = useState(false);
  if (!user || user.role !== 'admin') return null;

  const fire = async (relaxed) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/breakaway/fire${relaxed ? '?relaxed=1' : ''}`, {}, { withCredentials: true });
      if (data.status === 'already_running') toast.info('Scan already running');
      const poll = setInterval(async () => {
        try {
          const st = (await axios.get(`${API}/admin/breakaway/status`, { withCredentials: true })).data;
          if (!st.in_progress) {
            clearInterval(poll); setBusy(false); onChanged();
            const s = st.last_summary;
            if (st.last_error) toast.error(`Scan failed: ${st.last_error}`);
            else if (s) toast.success(`Scan done — ${s.picks} pick(s) · ${s.tracked} tracked · ${s.candidates} gap-ups · ${s.in_universe} scanned`);
          }
        } catch { clearInterval(poll); setBusy(false); }
      }, 3000);
    } catch { toast.error('Scan failed to start'); setBusy(false); }
  };

  return (
    <div className="flex items-center gap-1.5 mb-1.5 flex-wrap" data-testid="breakaway-admin-controls">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground/70">Admin</span>
      <button
        type="button"
        onClick={() => fire(false)}
        disabled={busy}
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full ring-1 ring-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 hover:bg-amber-500/20 disabled:opacity-60"
        data-testid="breakaway-scan-now"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        Scan now
      </button>
      <button
        type="button"
        onClick={() => fire(true)}
        disabled={busy}
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full ring-1 ring-border text-muted-foreground hover:text-foreground disabled:opacity-60"
        title="Ignores the minute-bar freshness check so the pipeline can be tested while the market is closed"
        data-testid="breakaway-scan-test"
      >
        Test scan (closed market)
      </button>
      <button
        type="button"
        onClick={() => setShowBacktest(true)}
        className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full ring-1 ring-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-400 hover:bg-sky-500/20"
        data-testid="breakaway-backtest-open"
      >
        <History className="w-3 h-3" /> Backtest a day
      </button>
      <BreakawayBacktestDialog open={showBacktest} onOpenChange={setShowBacktest} universe={filter === 'dividend' ? 'dividend' : 'all'} />
    </div>
  );
}
